/**
 * REQ-04 — document naming convention: Grade – Subject – Theme.
 *
 * The title is a display name held on the Firestore record. Renaming a
 * resource changes only the record; the stored file keeps the unique name it
 * was uploaded with.
 */

export const NAME_SEPARATOR = ' – ';

export const NAMING_HINT = 'Grade – Subject – Theme, e.g. "Grade 10 – Mathematics – Fractions"';

/**
 * @param {{ gradeName?: string, subjectName?: string, theme?: string }} parts display labels
 * @returns {string} e.g. "Grade 10 – Mathematics – Fractions"
 */
export const generateTitle = ({ gradeName, subjectName, theme }) =>
  [gradeName, subjectName, theme]
    .map((part) => (part || '').trim().replace(/\s+/g, ' '))
    .filter(Boolean)
    .join(NAME_SEPARATOR);

/** Collapses whitespace so equivalent titles compare equal. */
export const normaliseTitle = (title) => (title || '').trim().replace(/\s+/g, ' ');

const fileExtension = (fileName) => {
  const match = /\.[A-Za-z0-9]{1,5}$/.exec(fileName || '');
  return match ? match[0].toLowerCase() : '';
};

const slug = (text) =>
  (text || '')
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[\s-]+/g, '_')
    .slice(0, 80) || 'resource';

/**
 * Unique Storage object name, keeping the original extension so downloads
 * open in the right application.
 */
export const storageFileName = ({ grade, subject, title, originalName }) =>
  `${grade}_${subject}_${slug(title)}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}${fileExtension(originalName)}`;

/**
 * Content-Disposition offering the title as the download filename. Titles
 * may contain non-ASCII characters (the en dash separator), so an ASCII
 * fallback is given alongside the RFC 5987 UTF-8 form.
 */
export const contentDisposition = (title, originalName) => {
  const name = `${(title || 'resource').replace(/["\\/:*?<>|]+/g, '').trim() || 'resource'}${fileExtension(originalName)}`;
  const ascii = name.replace(/[–—]/g, '-').replace(/[^\x20-\x7E]/g, '_');
  return `attachment; filename="${ascii}"; filename*=UTF-8''${encodeURIComponent(name)}`;
};
